import type { DelegationView } from '@nest-recruit/shared';
import { addDhakaDays } from '../../lib/dhaka-time';

/**
 * The dates of a cover arrangement (USER-05). A cover runs to the END of the Until day, so one day (From = Until) is a real cover;
 * only an Until before From, or a missing date, cannot be used. The page shows the sentence beside the dates (D38: it used to just grey
 * the button out with no reason).
 */
export function coverDatesProblem(from: string, until: string): string | null {
  if (from === '') return 'Pick a From date.';
  if (until === '') return 'Pick an Until date.';
  if (until < from) return "Until can't be before From.";
  return null;
}

/**
 * "Change dates" (review R19), the server's rules said before Save: the end can't be in the past (End now ends a cover today); a cover
 * already in force keeps its start; one still to come may move its start to today or later, never earlier. Dhaka days, `YYYY-MM-DD`.
 */
export function changeDatesProblem(
  dates: { readonly from: string; readonly until: string },
  original: { readonly from: string },
  today: string,
  hasStarted: boolean,
): { readonly field: 'from' | 'until'; readonly message: string } | null {
  const basic = coverDatesProblem(dates.from, dates.until);
  if (basic !== null) return { field: dates.from === '' ? 'from' : 'until', message: basic };
  if (dates.until < today) return { field: 'until', message: "The end can't be in the past. Use End now to finish the cover today." };
  if (hasStarted && dates.from !== original.from) {
    return { field: 'from', message: 'This cover has already started, so its start stays. Change the end only.' };
  }
  if (!hasStarted && dates.from !== original.from && dates.from < today) {
    return { field: 'from', message: "A cover can't start in the past. Pick today or later." };
  }
  return null;
}

type CoverTimes = Pick<DelegationView, 'startsAt' | 'endsAt' | 'revokedAt'>;

/**
 * Where a cover stands. Ended by hand while in force is "Ended early"; ended before it began is "Cancelled" (the row's Cancel button),
 * so the list never says a cover ended early that never started.
 */
export type CoverStatus = 'active' | 'scheduled' | 'finished' | 'ended_early' | 'cancelled';

export function coverStatus(cover: CoverTimes, now: number): CoverStatus {
  const starts = Date.parse(cover.startsAt);
  if (cover.revokedAt !== null) return Date.parse(cover.revokedAt) <= starts ? 'cancelled' : 'ended_early';
  if (now < starts) return 'scheduled';
  if (now > Date.parse(cover.endsAt)) return 'finished';
  return 'active';
}

export const COVER_STATUS_LABEL: Readonly<Record<CoverStatus, string>> = {
  active: 'Active now',
  scheduled: 'Scheduled',
  finished: 'Finished',
  ended_early: 'Ended early',
  cancelled: 'Cancelled',
};

const STATUS_ORDER: Readonly<Record<CoverStatus, number>> = { active: 0, scheduled: 1, finished: 2, ended_early: 2, cancelled: 2 };

/** Active first, then what is coming soonest, then the past with the most recent first. */
export function sortCovers<T extends CoverTimes>(covers: readonly T[], now: number): readonly T[] {
  return [...covers].sort((a, b) => {
    const order = STATUS_ORDER[coverStatus(a, now)] - STATUS_ORDER[coverStatus(b, now)];
    if (order !== 0) return order;
    if (coverStatus(a, now) === 'scheduled') return Date.parse(a.startsAt) - Date.parse(b.startsAt);
    return Date.parse(b.startsAt) - Date.parse(a.startsAt);
  });
}

/** The quick dates beside the date fields. Each one starts tomorrow: cover is arranged before you go. */
export type QuickCover = 'tomorrow' | 'one_week' | 'two_weeks';

export const QUICK_COVERS: readonly { readonly value: QuickCover; readonly label: string; readonly days: number }[] = [
  { value: 'tomorrow', label: 'Tomorrow', days: 1 },
  { value: 'one_week', label: '1 week', days: 7 },
  { value: 'two_weeks', label: '2 weeks', days: 14 },
];

/** From and Until (Dhaka days, `YYYY-MM-DD`) for a quick choice: "1 week" is tomorrow and the six days after it. */
export function quickCoverDates(quick: QuickCover, today: string): { readonly from: string; readonly until: string } {
  const days = QUICK_COVERS.find((choice) => choice.value === quick)?.days ?? 1;
  const from = addDhakaDays(today, 1);
  return { from, until: addDhakaDays(from, days - 1) };
}
